import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import apiClient, { formatApiError } from "@/api/client";
import { ChevronLeft, Plus, ListTree, Trash2, ChevronRight, Loader2, Tag } from "lucide-react";
import { toast } from "sonner";
import CustomerSelect from "@/components/CustomerSelect";

export default function KeywordListsPage() {
  const nav = useNavigate();
  const [lists, setLists] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [newName, setNewName] = useState("");
  const [newCustomer, setNewCustomer] = useState("");

  const load = async () => {
    try {
      const [a, b] = await Promise.all([
        apiClient.get("/keyword-lists"),
        apiClient.get("/customers"),
      ]);
      setLists(a.data || []); setCustomers(b.data || []);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!newName.trim()) return toast.error("Liste adı gerekli");
    if (!newCustomer) return toast.error("Müşteri seçimi zorunlu — listeyi bir müşteriye bağlamalısın");
    try {
      const { data } = await apiClient.post("/keyword-lists", { name: newName.trim(), customer_id: newCustomer });
      toast.success("Liste oluşturuldu");
      setShowCreate(false); setNewName(""); setNewCustomer("");
      nav(`/ai-araclar/anahtar-kelime/listeler/${data.id}`);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const remove = async (id) => {
    if (!window.confirm("Bu listeyi silmek istediğine emin misin?")) return;
    try { await apiClient.delete(`/keyword-lists/${id}`); load(); toast.success("Silindi"); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const customerName = (id) => customers.find(c=>c.id===id)?.name || "—";

  return (
    <div className="space-y-5" data-testid="kw-lists-page">
      <div className="flex items-center gap-2 text-xs text-[#64748B]">
        <Link to="/ai-araclar/anahtar-kelime" className="hover:text-[#4F46E5] flex items-center gap-1"><ChevronLeft size={14}/>Anahtar Kelime Analizi</Link>
        <span>/</span>
        <span className="font-medium text-[#0F172A]">Kayıtlı Listeler</span>
      </div>

      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex items-start gap-3">
          <div className="w-11 h-11 rounded-xl bg-[#EEF2FF] flex items-center justify-center shrink-0"><ListTree size={20} className="text-[#4F46E5]"/></div>
          <div>
            <h1 className="text-2xl font-bold">Kayıtlı Anahtar Kelime Listeleri</h1>
            <p className="text-sm text-[#64748B] mt-1 max-w-2xl">Müşteri bazlı pozitif/negatif kelime listeleri. Analizden seçerek veya manuel ekleyerek oluştur. Reklam Metni Üretici'de kullan.</p>
          </div>
        </div>
        <button onClick={()=>setShowCreate(true)} className="ky-btn ky-btn-accent" data-testid="create-list-btn"><Plus size={14}/>Yeni Liste</button>
      </div>

      {showCreate && (
        <div className="ky-card p-4 space-y-3 border-2 border-[#C7D2FE]">
          <h3 className="font-semibold text-sm">Yeni Liste</h3>
          <input value={newName} onChange={e=>setNewName(e.target.value)} placeholder="Liste adı (örn: İstanbul SEO Kampanyası) *" className="ky-input" data-testid="new-list-name" autoFocus/>
          <CustomerSelect customers={customers} value={newCustomer || null} onChange={(v) => setNewCustomer(v || "")} placeholder="— müşteri seç (zorunlu) —" required testid="new-list-customer"/>
          {newCustomer ? (
            <p className="text-[10px] text-[#10B981]">✓ Liste müşteri profili + portalında görünecek</p>
          ) : (
            <p className="text-[10px] text-[#EF4444]">⚠️ Müşteri seçimi zorunlu</p>
          )}
          <div className="flex justify-end gap-2">
            <button onClick={()=>setShowCreate(false)} className="ky-btn ky-btn-outline ky-btn-sm">İptal</button>
            <button onClick={create} className="ky-btn ky-btn-accent ky-btn-sm" data-testid="confirm-create-list">Oluştur</button>
          </div>
        </div>
      )}

      <div className="ky-card p-0 overflow-hidden">
        {loading && <div className="p-10 text-center text-sm text-[#94A3B8]"><Loader2 size={14} className="inline animate-spin mr-2"/>Yükleniyor…</div>}
        {!loading && lists.length === 0 && (
          <div className="p-12 text-center">
            <ListTree size={32} className="mx-auto text-[#CBD5E1] mb-3"/>
            <div className="text-sm font-medium text-[#0F172A]">Henüz liste yok</div>
            <p className="text-xs text-[#94A3B8] mt-1">İlk listeni oluşturarak başla.</p>
          </div>
        )}
        {!loading && lists.map(l => (
          <div key={l.id} className="px-5 py-3 border-b border-[#ECECEF] last:border-b-0 hover:bg-[#FAFAFB] flex items-center gap-3" data-testid={`list-row-${l.id}`}>
            <div onClick={()=>nav(`/ai-araclar/anahtar-kelime/listeler/${l.id}`)} className="flex-1 min-w-0 cursor-pointer">
              <div className="flex items-center gap-2 mb-0.5">
                <Tag size={13} className="text-[#4F46E5]"/>
                <span className="font-medium text-sm truncate">{l.name}</span>
              </div>
              <div className="text-[11px] text-[#94A3B8]">
                {customerName(l.customer_id)} · {(l.positives||[]).length} pozitif · {(l.negatives||[]).length} negatif · {new Date(l.updated_at).toLocaleString("tr-TR")}
              </div>
            </div>
            <button onClick={()=>nav(`/ai-araclar/anahtar-kelime/listeler/${l.id}`)} className="ky-btn ky-btn-ghost ky-btn-icon"><ChevronRight size={14}/></button>
            <button onClick={()=>remove(l.id)} className="ky-btn ky-btn-ghost ky-btn-icon text-[#EF4444]"><Trash2 size={13}/></button>
          </div>
        ))}
      </div>
    </div>
  );
}
